import { useMemo, useState } from "react";

/**
 * A dropdown that lets the user either pick an existing row from a list OR
 * type a brand-new name. The wizard's save orchestrator decides whether to
 * POST a new record (when `value.newName` is set) or reuse an existing id
 * (when `value.id` is set).
 *
 * Why not a rich combobox? A native <select> + a small "+ New …" option is
 * familiar to admin users, keyboard-friendly for free, and avoids pulling
 * in a combobox library. The tradeoff: can't search-as-you-type a long
 * list. For the lookups used in admissions (SY, semester, grade level,
 * section) the lists are short enough that browse works fine.
 *
 * value shape:  { id: number } | { newName: string } | null
 */
export default function TypeableCombo({
  label, required,
  value, onChange,
  items,                           // [{ id, label }]
  newPlaceholder = "New name",
  s,                               // parent's CSS module
  disabled,
}) {
  const [dirty, setDirty] = useState(false);

  // Flag possible typo duplicates — case-insensitive compare against existing
  // labels. This is the safeguard I flagged in the design convo: the combo
  // lets the user type "Grade 1A" even when "Grade 1 A" exists, so we warn.
  const warnDupOf = useMemo(() => {
    if (!value || !value.newName) return null;
    const norm = value.newName.trim().toLowerCase().replace(/\s+/g, "");
    const hit = items.find(it => (it.label || "").toLowerCase().replace(/\s+/g, "") === norm);
    return hit ? hit.label : null;
  }, [value, items]);

  const selectValue =
    value && value.id != null   ? String(value.id)
    : value && value.newName != null ? "__new"
    : "";

  function onSelect(e) {
    setDirty(true);
    const v = e.target.value;
    if (v === "")          { onChange(null);                return; }
    if (v === "__new")     { onChange({ newName: "" });    return; }
    onChange({ id: Number(v) });
  }

  function onNameChange(e) {
    onChange({ newName: e.target.value });
  }

  return (
    <div className={s.field}>
      <label className={s.fieldLabel}>
        {label}{required && <span className={s.req}>*</span>}
      </label>
      <select className={s.fieldSelect}
              value={selectValue}
              onChange={onSelect}
              disabled={disabled}>
        <option value="">— select —</option>
        {items.map(it => (
          <option key={it.id} value={it.id}>{it.label}</option>
        ))}
        <option value="__new">+ Create new…</option>
      </select>
      {value?.newName != null && (
        <>
          <input className={s.fieldInput}
                 type="text"
                 autoFocus={dirty}
                 placeholder={newPlaceholder}
                 value={value.newName}
                 onChange={onNameChange}
                 disabled={disabled}
                 style={{ marginTop: 6 }} />
          {warnDupOf && (
            <div style={{
              fontSize: "0.78rem", color: "#b06628", marginTop: 4,
              fontStyle: "italic",
            }}>
              Did you mean existing &quot;{warnDupOf}&quot;? Typing a slightly
              different spelling will create a duplicate.
            </div>
          )}
        </>
      )}
    </div>
  );
}
